import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, allArticles } from '@/mocks/site-data';

export default function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === categoryId) || categories[0],
    [categoryId]
  );

  const categoryArticles = useMemo(
    () => allArticles.filter((a) => a.categoryId === currentCategory.id),
    [currentCategory.id]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [categoryId]);

  const handleCategoryChange = (slug: string) => {
    navigate(`/category/${slug}`);
  };

  return (
    <>
      <SchemaOrg
        type="category"
        categoryName={currentCategory.name}
      />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <section className="py-14 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700">{currentCategory.name}</span>
            </nav>

            <div className="mb-10">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-500 max-w-2xl">
                {currentCategory.description} — 共 {categoryArticles.length} 篇文章
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap mb-10">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    cat.id === currentCategory.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group flex flex-col bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-200 cursor-pointer"
                >
                  <div className="p-5 flex flex-col flex-1">
                    <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-4 flex-1">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span>{article.readTime}</span>
                      <span>{article.publishDate}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {categoryArticles.length === 0 && (
              <div className="text-center py-16">
                <p className="text-foreground-400">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}